import { Event } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";
import { getCurrencySymbol } from "@/lib/currencyUtils";

interface VIPSeatingTemplateProps {
  event: Event;
  vipTicketCount: number;
  standingTickets: number;
  earlyBirdTickets: number;
  onVipTicketChange: (increment: boolean) => void;
  onStandingTicketChange: (increment: boolean) => void;
  onEarlyBirdTicketChange: (increment: boolean) => void;
}

export const VIPSeatingTemplate = ({
  event,
  vipTicketCount,
  standingTickets,
  earlyBirdTickets,
  onVipTicketChange,
  onStandingTicketChange,
  onEarlyBirdTicketChange,
}: VIPSeatingTemplateProps) => {
  const currencySymbol = getCurrencySymbol(event.currency);
  
  console.log("VIP Template Props:", {
    event,
    vipTicketCount,
    standingTickets,
    earlyBirdTickets,
    maxVipTickets: event.quantity_vip,
    maxStandingTickets: event.quantity_standing,
    maxEarlyBirdTickets: event.quantity_early_bird
  });
  
  return (
    <div className="space-y-8">
      <div className="grid gap-6">
        {/* VIP Section */}
        <div className="bg-gray-700 p-6 rounded-lg">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <h3 className="text-2xl font-bold text-white">{event.ticket_title_vip || 'VIP: Meet and Greet'}</h3>
                {event.ticket_description_vip && (
                  <p className="text-gray-400 italic mt-1">{event.ticket_description_vip}</p>
                )}
              </div>
              <div className="flex items-center gap-4 mt-4 md:mt-0">
                <span className="text-2xl font-bold text-white">{currencySymbol}{event.price_vip}</span>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onVipTicketChange(false)}
                    disabled={vipTicketCount === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-bold text-white min-w-[3ch]">
                    {vipTicketCount}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onVipTicketChange(true)}
                    disabled={vipTicketCount >= (event.quantity_vip || 0)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {event.quantity_vip === 0 && (
              <p className="text-sm text-red-400">Sold Out</p>
            )}
          </div>
        </div>

        {/* Early Bird */}
        {event.early_bird_price && event.early_bird_price > 0 && (
          <div className="bg-gray-700 p-6 rounded-lg">
            <div className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">{event.ticket_title_early_bird || 'Early Bird'}</h3>
                  {event.ticket_description_early_bird && (
                    <p className="text-gray-400 italic mt-1">{event.ticket_description_early_bird}</p>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                  <span className="text-2xl font-bold text-white">{currencySymbol}{event.early_bird_price}</span>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onEarlyBirdTicketChange(false)}
                      disabled={earlyBirdTickets === 0}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-2xl font-bold text-white min-w-[3ch]">
                      {earlyBirdTickets}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onEarlyBirdTicketChange(true)}
                      disabled={!event.quantity_early_bird || earlyBirdTickets >= event.quantity_early_bird}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {event.quantity_early_bird === 0 && (
                <p className="text-sm text-red-400">Sold Out</p>
              )}
            </div>
          </div>
        )}

        {/* General Admission */}
        <div className="bg-gray-700 p-6 rounded-lg">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <h3 className="text-2xl font-bold text-white">{event.ticket_title_general || 'General Admission'}</h3>
                {event.ticket_description_general && (
                  <p className="text-gray-400 italic mt-1">{event.ticket_description_general}</p>
                )}
              </div>
              <div className="flex items-center gap-4 mt-4 md:mt-0">
                <span className="text-2xl font-bold text-white">{currencySymbol}{event.price_general_admission}</span>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onStandingTicketChange(false)}
                    disabled={standingTickets === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-2xl font-bold text-white min-w-[3ch]">
                    {standingTickets}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onStandingTicketChange(true)}
                    disabled={!event.quantity_standing || standingTickets >= event.quantity_standing}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            {event.quantity_standing === 0 && (
              <p className="text-sm text-red-400">Sold Out</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};